import { dateTermVoService } from '../../domain/value-object/DateTermVO'
import DateTimeTermVO from '../../domain/value-object/DateTimeTermVO'
import {
  TextMatchCondition,
  TextMatchMode,
  WbsItemSearchConditionV2VO,
} from '../../domain/value-object/WbsItemSearchConditionV2Vo'
import objects from '../../utils/objects'
import DateTimeVO from '../../domain/value-object/DateTimeVO'
import { ApiResponseValue } from '../model/api'
import { adaptSearchConditionBefore20230125 } from '../compatible-adaptors/wbsItemSearchConditionCompatibleAdaptor'

// V1の検索条件（string または string[]）を V2 の TextMatchCondition[] に変換する。
// V1では mode の指定がなかったため、デフォルトで 'include' を付与する。
// 既に V2 形式（TextMatchCondition[]）の場合はそのまま返す。
const migrateTextMatchCondition = (
  value: unknown
): TextMatchCondition[] | undefined => {
  if (!value) return undefined
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined
    if (typeof value[0] === 'string') {
      return value.map(v => ({ value: v, mode: 'include' as TextMatchMode }))
    }
    return value as TextMatchCondition[]
  }
  if (typeof value === 'string') {
    return value ? [{ value, mode: 'include' }] : undefined
  }
  return undefined
}

type WbsItemSearchConditionV2Factory = {
  fromApiResponseToVo: (src: ApiResponseValue) => WbsItemSearchConditionV2VO
}

export const useWbsItemSearchConditionV2Factory =
  (): WbsItemSearchConditionV2Factory => {
    const fromApiResponseToVo = (
      src: ApiResponseValue
    ): WbsItemSearchConditionV2VO => {
      if (!src) return {}
      const adapted = adaptSearchConditionBefore20230125(src)
      const dest: WbsItemSearchConditionV2VO = {}
      for (const entry of Object.entries(adapted)) {
        const key = entry[0]
        const value = entry[1]
        if (!value) continue
        if (key === 'searchText') continue
        if (['code', 'displayName', 'description', 'tagUuids'].includes(key)) {
          const migrated = migrateTextMatchCondition(value)
          if (migrated) objects.setValue(dest, key, migrated)
          continue
        }
        // Migrate old single-value member fields to new array fields
        if (
          key === 'teamUuid' ||
          key === 'accountableUuid' ||
          key === 'responsibleUuid' ||
          key === 'assigneeUuid' ||
          key === 'createdByUuid' ||
          key === 'updatedByUuid'
        ) {
          if (typeof value === 'string' && value) {
            const newKey = `${key}s` as keyof WbsItemSearchConditionV2VO
            objects.setValue(dest, newKey, [value])
          }
          continue
        }
        if (
          [
            'scheduledStartDate',
            'scheduledEndDate',
            'actualStartDate',
            'actualEndDate',
          ].includes(key)
        ) {
          if (typeof value !== 'object' || Array.isArray(value)) continue
          const v = dateTermVoService.construct(value['from'], value['to'])
          objects.setValue(dest, key, v)
          continue
        }
        if (['createdAt', 'updatedAt'].includes(key)) {
          if (typeof value !== 'object' || Array.isArray(value)) continue
          const v = new DateTimeTermVO(
            !!value['from'] ? new DateTimeVO(value['from']) : undefined,
            !!value['to'] ? new DateTimeVO(value['to']) : undefined
          )
          objects.setValue(dest, key, v)
          continue
        }
        objects.setValue(dest, key, value)
      }
      return dest
    }
    return {
      fromApiResponseToVo,
    }
  }
